import { FC } from 'react';

import addGrantAutoAssignedImage from '../../../assets/images/add-grant-1.png';
import addGrantImage from '../../../assets/images/add-grant-2.png';
import addGrantTemplateStepImage from '../../../assets/images/add-grant-template-step.png';
import mapFieldCustomImage from '../../../assets/images/map-field-custom.png';
import mapFieldSystemImage from '../../../assets/images/map-field-system.png';
import signatureFieldsImage from '../../../assets/images/signature-fields.png';
import signaturePlacementImage from '../../../assets/images/signature-placement.png';
import signaturePlacementImage2 from '../../../assets/images/signature-placement-2.png';

const tokenFontStyle = { fontFamily: '"Courier New", Courier, monospace' };

export const TemplatePreparation: FC = () => (
  <div>
    <div className="flex flex-col gap-2">
      <span className="text-[18px] font-semibold text-gray-700">Template Mapping Guide</span>
      <span className="text-xs text-gray-700">
        Learn how to prepare your Word document for use in CapQuest, add the right placeholders, and
        map them to real data so your documents are generated automatically.
      </span>
    </div>

    <h1 className="mb-1 mt-3 text-[16px] font-semibold text-gray-700" id="main-step-1">
      Before you start — key things to know
    </h1>
    <ul className="flex list-disc flex-col gap-2 pl-10">
      <li className="text-xs font-[450] text-gray-700">
        Templates are reusable — configure once, generate documents as many times as needed
      </li>
      <li className="text-xs font-[450] text-gray-700">
        Your file must be a <strong>.docx</strong> and no larger than <strong>10 MB</strong>
      </li>
      <li className="text-xs font-[450] text-gray-700">
        Use{' '}
        <span style={{ ...tokenFontStyle, color: 'var(--color-brand-900)' }}>
          {'{{variable_name}}'}
        </span>{' '}
        for data that CapQuest fills in automatically (e.g. stakeholder name, grant date)
      </li>
      <li className="text-xs font-[450] text-gray-700">
        Use{' '}
        <span style={{ ...tokenFontStyle, color: 'var(--color-brand-900)' }}>
          {'[standard_field]'}
        </span>{' '}
        to place signature, date signed, initials, or text fields for signers
      </li>
      <li className="text-xs font-[450] text-gray-700">
        Placeholder names: letters, numbers, and underscores only —{' '}
        <strong> no spaces, no special characters, no empty placeholders </strong>
      </li>
      <li className="text-xs font-[450] text-gray-700">
        If you need two different people of the same stakeholder type to sign,{' '}
        <strong>pre-assign specific individuals</strong> to each token during mapping — otherwise
        both tokens resolve to the same person
      </li>
      <li className="text-xs font-[450] text-gray-700">
        If a variable has no matching CapQuest field, you can enter a{' '}
        <strong>custom text value</strong> — it will be hardcoded into every document generated from
        this template
      </li>
      <li className="text-xs font-[450] text-gray-700">
        Editing a template <strong> never affects </strong> documents already created — only future
        ones
      </li>
      <li className="text-xs font-[450] text-gray-700">
        Leave at least <strong>one empty line above and below</strong> every standard field token
        for accurate placement
      </li>
      <li className="text-xs font-[450] text-gray-700">
        Questions or need help with mapping? Contact{' '}
        <a className="font-semibold text-brand-700 underline" href="mailto:support@capquest.io">
          support@capquest.io
        </a>
      </li>
    </ul>

    <h1 className="mb-1 mt-6 text-[16px] font-semibold text-gray-700" id="main-step-2">
      What is a template and how to work with it
    </h1>
    <div className="flex flex-col gap-2">
      <span className="text-xs font-[450] text-gray-700">
        A <strong>document template</strong> in CapQuest is a reusable Word document that acts as
        the master version of any agreement or legal document you send through the platform. Once
        configured, it can be used to generate documents repeatedly — each one automatically filled
        with the correct data for that specific recipient and event.
      </span>
      <span className="text-xs font-[450] text-gray-700">
        You configure a template once. After that, every time you issue, for example, a grant, and
        select that template, CapQuest generates a fully populated, ready-to-sign document without
        any manual editing.
      </span>
    </div>

    <h1 className="mb-1 mt-3 text-[16px] font-semibold text-gray-700">Two types of placeholders</h1>
    <div className="flex flex-col gap-3" id="two-types-of-placeholders">
      <span className="text-xs font-[450] text-gray-700">
        Your document can contain two kinds of placeholders. They serve different purposes and use
        different syntax.
      </span>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <table className="w-full table-fixed border-collapse">
          <thead className="bg-[#F3F4F6]">
            <tr className="border-b border-gray-200">
              <th className="w-1/5 border-r border-gray-200 px-3 py-2 text-left text-xs font-semibold text-gray-700">
                Type
              </th>
              <th className="w-1/5 border-r border-gray-200 px-3 py-2 text-left text-xs font-semibold text-gray-700">
                Syntax
              </th>
              <th className="w-1/3 border-r border-gray-200 px-3 py-2 text-left text-xs font-semibold text-gray-700">
                Purpose
              </th>
              <th className="w-1/6 px-3 py-2 text-left text-xs font-semibold text-gray-700">
                Example
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-200">
              <td className="border-r border-gray-200 px-3 py-2 align-top text-xs font-semibold text-gray-700">
                Data variable
              </td>
              <td className="border-r border-gray-200 px-3 py-2 align-top text-xs font-[450] text-gray-900">
                <span style={{ ...tokenFontStyle, color: 'var(--color-brand-900)' }}>
                  {'{{variable_name}}'}
                </span>
              </td>
              <td className="border-r border-gray-200 px-3 py-2 align-top text-xs font-[450] text-gray-700">
                Replaced with real data from CapQuest (stakeholder name, grant amount, vesting
                schedule, etc.)
              </td>
              <td className="px-3 py-2 align-top text-xs font-[450] text-gray-900">
                <span style={{ ...tokenFontStyle, color: 'var(--color-brand-900)' }}>
                  {'{{StakeholderName}}'}
                </span>
              </td>
            </tr>
            <tr>
              <td className="border-r border-gray-200 px-3 py-2 align-top text-xs font-semibold text-gray-700">
                Standard field
              </td>
              <td className="border-r border-gray-200 px-3 py-2 align-top text-xs font-[450] text-gray-900">
                <span style={{ ...tokenFontStyle, color: 'var(--color-brand-900)' }}>
                  {'[field_name]'}
                </span>
              </td>
              <td className="border-r border-gray-200 px-3 py-2 align-top text-xs font-[450] text-gray-700">
                Marks where a signature, date signed, initials, or text input field should appear
                for a specific signer
              </td>
              <td className="px-3 py-2 align-top text-xs font-[450] text-gray-700">
                <span style={{ ...tokenFontStyle, color: 'var(--color-brand-900)' }}>
                  {'[signature_grantee]'}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <span className="text-xs font-[450] text-gray-700">
        Both types follow the same syntax rules and are detected automatically when you upload the
        file to CapQuest.
      </span>
    </div>

    <div className="flex flex-col gap-2" id="main-step-3">
      <h2 className="mb-1 mt-6 text-[16px] font-semibold text-gray-700">
        Step 1 — Prepare your document
      </h2>
      <span className="text-xs font-[450] text-gray-700">
        Your document must be a <strong>.docx file</strong> (Microsoft Word format) and no larger
        than <strong>10 MB</strong>. You write it like any normal agreement — but wherever you want
        CapQuest data to appear, or wherever a signature field should be, you insert a placeholder
        instead of a fixed value.
      </span>
    </div>
    <div className="mb-4 mt-4 flex flex-col rounded-lg bg-[#E6F7F0] p-3">
      <span className="mb-2 text-xs font-semibold text-gray-700">
        <span className="mr-2">💡</span> Don't have a template yet?{' '}
      </span>
      <span className="text-xs font-[450] text-gray-700">
        Depending on the document category you select, CapQuest may offer a sample template to
        download on the upload step. It already contains common placeholders ready for mapping — a
        great starting point you can customise for your needs.
      </span>
    </div>

    <h2 className="mt-5 text-[16px] font-semibold text-gray-700">
      Data variables — {'{{variable_name}}'}
    </h2>
    <p className="text-xs font-[450] text-gray-700">
      Use double curly braces wherever you want CapQuest to automatically fill in data. When a
      document is generated from this template, each variable is replaced with the actual value for
      that specific event and recipient.
    </p>
    <img
      alt="Signature placement"
      className="mx-auto mt-4 h-auto w-[800px]"
      src={signaturePlacementImage2}
    />
    <div className="mb-4 mt-4 flex flex-col rounded-lg bg-[#FFFBEB] p-3">
      <span className="mb-2 text-xs font-semibold text-gray-700">
        <span className="mr-2">⚠️</span> Field availability depends on category{' '}
      </span>
      <span className="text-xs font-[450] text-gray-700">
        CapQuest only shows mapping options that are relevant to the document category and
        configuration selected for this template. For example, in a grant agreement, vesting-related
        fields only appear if the selected equity plan has vesting configured. As more document
        categories are added in the future, their specific data fields will become available for
        mapping in the same way.
      </span>
    </div>
    <div>
      <h2 className="mb-2 mt-5 text-[14px] font-semibold text-gray-700">
        Example usage in your document:
      </h2>
      <p className="mb-1 text-xs font-[450] text-gray-700">
        Instead of writing "This agreement is entered into between Acme Corp and John Smith", write:
      </p>
      <p className="mb-1 text-xs font-[450] italic text-gray-700">
        This agreement is entered into between {'{{CompanyName}}'} and {'{{StakeholderName}}'}
      </p>
    </div>
    <div>
      <h2 className="mb-2 mt-5 text-[14px] font-semibold text-gray-700">
        Standard fields — [field_name]
      </h2>
      <p className="mb-1 text-xs font-[450] text-gray-700">
        Use square brackets to mark where a signature, initials, date signed, or free text input
        field should appear in the document. These are the actual interactive signing fields that
        appear when the document is sent for e-signature via DocuSign. Standard fields work the same
        way regardless of document category or type.
      </p>
      <img
        alt="Signature placement"
        className="mx-auto mt-4 h-auto w-[550px]"
        src={signaturePlacementImage}
      />
    </div>
    <div>
      <h2 className="mb-2 mt-5 text-[14px] font-semibold text-gray-700">
        Available standard field types:
      </h2>
      <div className="w-[700px] overflow-hidden border border-gray-200 bg-white">
        <table className="w-[700px] table-fixed border-collapse">
          <thead className="bg-[#F3F4F6]">
            <tr className="border-b border-gray-200">
              <th className="w-1/4 border-r border-gray-200 px-3 py-2 text-left text-xs font-semibold text-gray-700">
                Field type
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
                What it does
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-200">
              <td className="border-r border-gray-200 px-3 py-2 align-top text-xs font-semibold text-gray-700">
                Signature
              </td>
              <td className="px-3 py-2 align-top text-xs font-[450] text-gray-700">
                Places a signature block at this position for the assigned signer
              </td>
            </tr>
            <tr className="border-b border-gray-200">
              <td className="border-r border-gray-200 px-3 py-2 align-top text-xs font-semibold text-gray-700">
                Date Signed
              </td>
              <td className="px-3 py-2 align-top text-xs font-[450] text-gray-700">
                Auto-fills with the date the signer signs the document
              </td>
            </tr>
            <tr className="border-b border-gray-200">
              <td className="border-r border-gray-200 px-3 py-2 align-top text-xs font-semibold text-gray-700">
                Initials
              </td>
              <td className="px-3 py-2 align-top text-xs font-[450] text-gray-700">
                Places an initials field at this position for the assigned signer
              </td>
            </tr>
            <tr>
              <td className="border-r border-gray-200 px-3 py-2 align-top text-xs font-semibold text-gray-700">
                Text
              </td>
              <td className="px-3 py-2 align-top text-xs font-[450] text-gray-700">
                Allows the signer to type a free-text value at this position
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mb-4 mt-4 text-xs font-[450] text-gray-700">
        You can place as many standard fields as needed — one per position. Each token in the
        document becomes one signing field, placed exactly where the token sits in the layout.
      </p>
      <div className="mt-6 flex flex-col gap-1">
        <span className="text-sm font-semibold text-gray-700">One token = one field placement</span>
        <span className="text-xs font-[450] text-gray-700">
          If you use the same token name more than once in the document, the same field type and
          signer assignment will apply to every occurrence.
        </span>
      </div>
      <div className="mt-6 flex flex-col gap-1">
        <h2 className="text-sm font-semibold text-gray-700">Naming your placeholders</h2>
        <span className="text-xs font-[450] text-gray-700">
          Both {'{{variables}}'} and [standard fields] accept free-form names — you choose what to
          call them. However, choosing meaningful names makes mapping and later grant creation much
          easier, especially when you have multiple signers of the same stakeholder type.
        </span>
        <span className="mt-4 text-sm font-semibold text-gray-700">For data variables</span>
        <span className="text-xs font-[450] text-gray-700">
          Name them after the data they will contain:
        </span>
        <ul className="flex list-disc flex-col gap-1 pl-10">
          <li className="text-xs font-[450] text-gray-700">
            <span style={tokenFontStyle}>
              {'{{StakeholderName}}, {{GrantDate}}, {{GrantAmount}}'}
            </span>
          </li>
          <li className="text-xs font-[450] text-gray-700">
            <span style={tokenFontStyle}>{'{{VestingDuration}}, {{Cliff}}, {{StrikePrice}}'}</span>
          </li>
        </ul>
        <span className="mt-4 text-sm font-semibold text-gray-700">For standard fields</span>
        <span className="text-xs font-[450] text-gray-700">
          Name them to reflect both what the field does and who is signing. This becomes especially
          important when multiple people of the same stakeholder type need to sign — for example,
          two Founders.
        </span>
        <span className="text-xs font-[450] text-gray-700">
          <strong>Recommended pattern:</strong>{' '}
          <span style={tokenFontStyle}>[fieldtype_signeridentifier]</span>
        </span>
        <div className="mt-3 overflow-hidden border border-gray-200 bg-white">
          <table className="w-full table-fixed border-collapse">
            <thead className="bg-[#F3F4F6]">
              <tr className="border-b border-gray-200">
                <th className="w-1/2 border-r border-gray-200 px-3 py-2 text-left text-sm font-semibold text-gray-700">
                  ✓ Good examples
                </th>
                <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">
                  ✗ Avoid these
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border-r border-gray-200 bg-[#E6F7F0] px-3 py-2 align-top text-xs font-[450] text-gray-700">
                  <div className="flex flex-col gap-1">
                    <span style={tokenFontStyle}>[signature_grantee]</span>
                    <span style={tokenFontStyle}>[datesigned_grantee]</span>
                    <span style={tokenFontStyle}>[signature_founder1]</span>
                    <span style={tokenFontStyle}>[signature_founder2]</span>
                    <span style={tokenFontStyle}>[initials_boardmember]</span>
                    <span className="mt-1 italic text-gray-500">
                      Clear, descriptive, and uniquely identifiable. When two Founders need to sign,
                      [signature_founder1] and [signature_founder2] make it obvious which is which
                      during mapping.
                    </span>
                  </div>
                </td>
                <td className="bg-[#FEF2F2] px-3 py-2 align-top text-xs font-[450] text-gray-700">
                  <div className="flex flex-col gap-1">
                    <span style={tokenFontStyle}>[s1]</span>
                    <span style={tokenFontStyle}>[field1]</span>
                    <span style={tokenFontStyle}>[sign]</span>
                    <span style={tokenFontStyle}>[x]</span>
                    <span className="mt-1 italic text-gray-500">
                      Technically valid, but when you return to map these weeks later — or when a
                      colleague edits the template — it's impossible to know which signer or field
                      type these refer to.
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <h2 className="mt-8 text-sm font-semibold text-gray-700">Syntax rules</h2>
        <span className="text-xs font-[450] text-gray-700">
          Both <span style={tokenFontStyle}>{'{{variables}}'}</span> and{' '}
          <span style={tokenFontStyle}>[standard_fields]</span> follow the same rules. Violating any
          of these will cause CapQuest to reject the file on upload.
        </span>
        <div className="mt-3 overflow-hidden border border-gray-200 bg-white">
          <table className="w-full table-fixed border-collapse">
            <thead className="bg-[#F3F4F6]">
              <tr className="border-b border-gray-200">
                <th className="w-5/12 border-r border-gray-200 px-3 py-2 text-left text-sm font-semibold text-gray-700">
                  Rule
                </th>
                <th className="w-3/12 border-r border-gray-200 px-3 py-2 text-left text-sm font-semibold text-gray-700">
                  ✓ Valid
                </th>
                <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">
                  ✗ Invalid
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-200">
                <td className="border-r border-gray-200 px-3 py-2 align-top text-xs font-[450] text-gray-700">
                  Only letters (A–Z, a–z), numbers (0–9), and underscores (_)
                </td>
                <td className="border-r border-gray-200 bg-[#E6F7F0] px-3 py-2 align-top text-xs font-[450] text-gray-700">
                  <span style={tokenFontStyle}>{'{{GrantDate}}'}</span>
                </td>
                <td className="bg-[#FEF2F2] px-3 py-2 align-top text-xs font-[450] text-gray-700">
                  <span style={tokenFontStyle}>{'{{Grant Date}}'}</span>
                </td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="border-r border-gray-200 px-3 py-2 align-top text-xs font-[450] text-gray-700">
                  No spaces inside the placeholder
                </td>
                <td className="border-r border-gray-200 bg-[#E6F7F0] px-3 py-2 align-top text-xs font-[450] text-gray-700">
                  <span style={tokenFontStyle}>[signature_grantee]</span>
                </td>
                <td className="bg-[#FEF2F2] px-3 py-2 align-top text-xs font-[450] text-gray-700">
                  <span style={tokenFontStyle}>[signature grantee]</span>
                </td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="border-r border-gray-200 px-3 py-2 align-top text-xs font-[450] text-gray-700">
                  No special characters (hyphens, dots, slashes, etc.)
                </td>
                <td className="border-r border-gray-200 bg-[#E6F7F0] px-3 py-2 align-top text-xs font-[450] text-gray-700">
                  <span style={tokenFontStyle}>{'{{StrikePrice}}'}</span>
                </td>
                <td className="bg-[#FEF2F2] px-3 py-2 align-top text-xs font-[450] text-gray-700">
                  <span style={tokenFontStyle}>{'{{Strike-Price}}'}</span>
                </td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="border-r border-gray-200 px-3 py-2 align-top text-xs font-[450] text-gray-700">
                  Placeholder must not be empty
                </td>
                <td className="border-r border-gray-200 bg-[#E6F7F0] px-3 py-2 align-top text-xs font-[450] text-gray-700">
                  <span style={tokenFontStyle}>{'{{StakeholderName}}'}</span>
                </td>
                <td className="bg-[#FEF2F2] px-3 py-2 align-top text-xs font-[450] text-gray-700">
                  <span style={tokenFontStyle}>{'{{}} or []'}</span>
                </td>
              </tr>
              <tr>
                <td className="border-r border-gray-200 px-3 py-2 align-top text-xs font-[450] text-gray-700">
                  Single continuous token — no nesting
                </td>
                <td className="border-r border-gray-200 bg-[#E6F7F0] px-3 py-2 align-top text-xs font-[450] text-gray-700">
                  <span style={tokenFontStyle}>{'{{GrantAmount}}'}</span>
                </td>
                <td className="bg-[#FEF2F2] px-3 py-2 align-top text-xs font-[450] text-gray-700">
                  <span style={tokenFontStyle}>{'{{Grant{{Amount}}}}'}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-6 flex w-[800px] flex-col rounded-lg bg-[#FEF2F2] p-4">
          <span className="mb-3 text-xs font-semibold text-gray-700">
            <span className="mr-2">🚫</span> Invalid placeholders will block upload
          </span>
          <span className="text-xs font-[450] text-gray-700">
            When you upload a file, CapQuest scans it automatically. If any placeholders fail the
            syntax check, you'll see an error listing exactly which ones are invalid. Fix them in
            your Word document and re-upload.
          </span>
        </div>
        <h2 className="mt-6 text-[16px] font-semibold text-gray-700">Using the sample template</h2>
        <span className="text-xs font-[450] text-gray-700">
          On the document upload step, CapQuest may offer a sample template to download depending on
          the document category you selected. This is a ready-made Word document with common
          placeholders already inserted and formatted correctly — you can use it as-is or customise
          it for your needs.
        </span>
      </div>
    </div>

    <div className="flex flex-col gap-2 pt-8" id="main-step-4">
      <h2 className="text-[16px] font-semibold leading-[2] text-gray-700">
        Step 2 — Map fields in CapQuest
      </h2>
      <span className="text-xs font-[450] text-gray-700">
        Once you upload your file, CapQuest scans it and detects all{' '}
        <span style={tokenFontStyle}>{'{{variables}}'}</span> and{' '}
        <span style={tokenFontStyle}>[standard fields]</span> automatically, if syntax is correct.
        You then map each one in the Map Fields step. This process is the same regardless of
        document category.
      </span>
      <div className="mt-6 flex flex-col gap-2">
        <h3 className="text-sm font-semibold text-gray-700">1. Variables panel (right side)</h3>
        <span className="text-xs font-[450] text-gray-700">
          Each <span style={tokenFontStyle}>{'{{variable}}'}</span> found in your document is listed
          here. For each one, select the corresponding CapQuest system field from the dropdown — for
          example, map <span style={tokenFontStyle}>{'{{GrantDate}}'}</span> to{' '}
          <strong>Grant Date</strong>, or{' '}
          <span style={tokenFontStyle}>{'{{StakeholderName}}'}</span> to{' '}
          <strong>Stakeholder Name</strong>.
        </span>
        <span className="text-xs font-[450] text-gray-700">
          If no matching system field exists for a placeholder, you can enter a{' '}
          <strong>Custom Text Value</strong> instead (see section below).
        </span>
      </div>
      <img alt="Map field system" className="h-auto w-[300px]" src={mapFieldSystemImage} />
      <div className="mt-6 flex flex-col gap-2">
        <h3 className="text-[14px] font-semibold text-gray-700">Custom text values</h3>
        <span className="text-xs font-[450] text-gray-700">
          If you include a <span style={tokenFontStyle}>{'{{variable}}'}</span> in your document
          that doesn't correspond to any CapQuest system field — for example, a clause number, a
          governing law jurisdiction, or any static company-specific text — you can enter a{' '}
          <strong>Custom Text Value</strong> instead of selecting a field from the dropdown.
        </span>
        <img alt="Map field custom" className="mt-4 h-auto w-[300px]" src={mapFieldCustomImage} />
        <div className="mt-6 flex flex-col gap-2">
          <span className="text-sm font-semibold text-gray-700">
            <span className="mr-2">📌</span> How custom text works
          </span>
          <span className="text-xs font-[450] text-gray-700">
            Once saved, the custom text is "hardcoded" into this template. Every document generated
            from the template will use that same text in that position — it cannot be changed during
            grant creation or signing. If you need to update it later, simply edit the template and
            change the custom text value there. You can also switch it back to a CapQuest system
            field mapping at any time.
          </span>
        </div>
        <div className="mt-6 border border-gray-300 bg-[#F3F4F6] p-3">
          <span className="text-sm font-semibold text-gray-700">Example use case</span>
          <span className="block text-xs font-[450] text-gray-700">
            Your agreement includes a <span style={tokenFontStyle}>{'{{GoverningLaw}}'}</span>{' '}
            placeholder which should always read "the laws of the Republic of Estonia". Since
            CapQuest doesn't have a Governing Law field, you enter the full text as a Custom Text
            Value. All future documents generated from this template will auto-fill that clause
            correctly — no manual editing needed at document creation time.
          </span>
        </div>
        <div className="mt-8 flex flex-col gap-2">
          <h3 className="text-[14px] font-semibold text-gray-700">
            2. Standard fields panel (left side)
          </h3>
          <span className="text-xs font-[450] text-gray-700">
            Each <span style={tokenFontStyle}>[standard_field]</span> found in your document is
            listed here. For each one, you must specify:
          </span>
          <ul className="flex list-disc flex-col gap-1 pl-10">
            <li className="text-xs font-[450] text-gray-700">
              <strong>Field Type</strong> — what kind of signing field this is: Signature, Date
              Signed, Initials, or Text
            </li>
            <li className="text-xs font-[450] text-gray-700">
              <strong>Stakeholder Type</strong> — which signatory role is responsible for this field
              (e.g. Grantee, Founder, Board Member). Only the signatory types you configured in the
              earlier step will appear
            </li>
            <li className="text-xs font-[450] text-gray-700">
              <strong>Signer (optional)</strong> — a specific named individual to pre-assign. If
              left blank, the person will be selected when creating the event.
            </li>
          </ul>
          <img
            alt="Signature fields"
            className="mt-2 h-auto w-[300px]"
            src={signatureFieldsImage}
          />
        </div>
      </div>
    </div>

    <div className="flex flex-col pt-4" id="mapping-signer-logic">
      <h2 className="text-[14px] font-semibold leading-[2] text-gray-700">
        Assigning standard fields — what it means for signers
      </h2>
      <span className="text-xs font-[450] text-gray-700">
        This is the most important part to understand before mapping. How you name your standard
        field tokens and assign them to stakeholder types directly controls{' '}
        <strong>who sees which signing field</strong> when the document is sent. This logic applies
        to all document types.
      </span>
      <div className="mt-4 flex flex-col">
        <h3 className="text-[14px] font-semibold text-gray-700">
          The core idea: one token = one signer slot
        </h3>
        <span className="text-xs font-[450] text-gray-700">
          Each <span style={tokenFontStyle}>[token]</span> in your document represents a{' '}
          <strong>specific signing position</strong>. When you assign it to a Stakeholder Type (e.g.
          Founder), you're saying: "this position belongs to a Founder." If you assign two tokens to
          the same stakeholder type without pre-selecting specific individuals, both tokens will
          resolve to the same person at event creation. To have two genuinely different people of
          the same type sign the document, you must pre-assign them individually during template
          mapping.
        </span>
      </div>
      <div className="mt-4 flex flex-col gap-2 border border-gray-200 bg-gray-50 p-3">
        <span className="text-xs font-semibold text-gray-700">
          Scenario 1: two Founders need to sign
        </span>
        <span className="text-xs font-[450] text-gray-700">
          Your document has two signature positions — one for each Founder. You add:
        </span>
        <ul className="flex list-disc flex-col gap-1 pl-10">
          <li className="text-xs font-[450] text-gray-700">
            <span style={tokenFontStyle}>[signature_founder1]</span> on page 3
          </li>
          <li className="text-xs font-[450] text-gray-700">
            <span style={tokenFontStyle}>[signature_founder2]</span> on page 3, next to it
          </li>
        </ul>
        <span className="mt-3 text-xs font-[450] text-gray-700">
          During mapping, you assign both tokens to Stakeholder Type = Founder — but you also use
          the optional Signer field to pre-assign a specific individual to each token: for example,
          John Doe to <span style={tokenFontStyle}>[signature_founder1]</span> and Olivia Bishoff to{' '}
          <span style={tokenFontStyle}>[signature_founder2]</span>.
        </span>
        <span className="mt-3 text-xs font-[450] text-gray-700">
          <strong>Result:</strong> Because each token has a specific person pre-assigned, both
          Founders are auto-filled at event creation. Each receives the document and sees their
          signature field in the correct position. If you had not pre-assigned them, both tokens
          would resolve to the same Founder — and the second signature slot would effectively be
          duplicated.
        </span>
        <img
          alt="Add grant recipients"
          className="mt-4 h-auto w-[400px]"
          src={addGrantAutoAssignedImage}
        />
        <span className="text-xs font-semibold text-gray-700">
          Scenario 2: two Founders need to sign — no specific signer pre-assigned
        </span>
        <span className="text-xs font-[450] text-gray-700">
          Your document has two signature positions for Founders. You add{' '}
          <span className="text-forest-500" style={tokenFontStyle}>
            [signature_founder1]
          </span>{' '}
          and{' '}
          <span className="text-forest-500" style={tokenFontStyle}>
            [signature_founder2]
          </span>
          , assign both to Stakeholder Type = Founder, and leave the Signer field empty on both.
        </span>
        <span className="mt-3 text-xs font-[450] text-gray-700">
          <strong>Result:</strong> At event creation, the system cannot distinguish between the two
          tokens — both are treated as the same Founder role with no individual tied to them. Only
          one Founder will appear as the signer, and both signature fields in the document will be
          assigned to that same person. Founder will receive one document with two signature fields,
          expecting two signature.
        </span>
        <img
          alt="Add grant auto assigned recipients"
          className="mt-2 h-auto w-[400px]"
          src={addGrantImage}
        />
        <span className="mt-3 text-xs font-[450] text-gray-700">
          <strong>What to do instead:</strong> If you need two different Founders to sign, go back
          to the template and use the Signer field to pre-assign a specific individual to each token
          — Founder A to{' '}
          <span className="text-forest-500" style={tokenFontStyle}>
            [signature_founder1]
          </span>{' '}
          and Founder B to{' '}
          <span className="text-forest-500" style={tokenFontStyle}>
            [signature_founder2]
          </span>
          . Once saved, both will be auto-filled correctly at every future document creation from
          this template.
        </span>
      </div>
      <div className="mt-6 flex flex-col rounded-lg bg-[#FFFBEB] p-4">
        <span className="mb-3 text-xs font-semibold text-gray-700">
          <span className="mr-2">⚠️</span> Same stakeholder type on multiple tokens = same person,
          unless you pre-assign
        </span>
        <span className="text-xs font-[450] text-gray-700">
          If you only have one token assigned to a stakeholder type (e.g. one{' '}
          <span style={tokenFontStyle}>[signature_founder]</span>) but you have not pre-assigned
          specific individuals, both tokens will resolve to the same person at event creation. To
          route each token to a different signer, use the optional Signer field during template
          mapping to pre-assign a specific individual to each token.
        </span>
      </div>
      <div className="mt-8 flex flex-col gap-2">
        <h3 className="text-[14px] font-semibold text-gray-700">
          Pre-assigning a specific individual (optional)
        </h3>
        <span className="text-xs font-[450] text-gray-700">
          For signers who are always the same person regardless of the document recipient (e.g. the
          CEO who always countersigns, or a fixed legal approver), you can pre-assign a specific
          individual in the template. This means at event creation, that signer is auto-filled and
          cannot be changed.
        </span>
        <span className="text-xs font-[450] text-gray-700">
          For auto-assigned roles (such as <strong>Grantee</strong> in a grant agreement, or{' '}
          <strong>Investor</strong> in a SAFE or fundraising document), pre-assignment is disabled —
          these roles are always filled by the recipients selected during event creation.
        </span>
      </div>
    </div>
    <hr className="mb-4 mt-10 border-gray-300" />
    <div className="flex flex-col gap-2 pt-2" id="main-step-5">
      <h2 className="text-[16px] font-semibold leading-[2] text-gray-700">
        What happens at document creation
      </h2>
      <span className="text-xs font-[450] text-gray-700">
        Once your template is saved and configured, here is what happens when you use a document
        using it:
      </span>
      <ul className="flex list-decimal flex-col gap-2 pl-10">
        <li className="text-xs font-[450] text-gray-700">
          You select this template from the available templates for the relevant event or document
          category.
        </li>
        <li className="text-xs font-[450] text-gray-700">
          CapQuest shows a <strong>Review Data Mapping</strong> view — you can verify what data will
          be filled into each variable for each recipient.
        </li>
        <li className="list-none">
          <img
            alt="Add grant template step"
            className="h-auto w-[450px]"
            src={addGrantTemplateStepImage}
          />
        </li>
        <li className="text-xs font-[450] text-gray-700">
          For auto-assigned signer roles (such as Grantee in a grant agreement), the signer is
          automatically filled from the recipients selected during document creation.
        </li>
        <li className="text-xs font-[450] text-gray-700">
          For other signatory roles (e.g. Founder, Board Member, Investor), you select the specific
          person from a dropdown — unless you pre-assigned someone in the template.
        </li>
        <li className="text-xs font-[450] text-gray-700">
          The document is generated with all data filled in and sent via DocuSign with signature
          fields placed exactly where your tokens were.
        </li>
      </ul>
    </div>

    <div className="flex flex-col pt-4" id="main-step-6">
      <h2 className="text-[16px] font-semibold leading-[2] text-gray-700">Editing a template</h2>
      <span className="text-xs font-[450] text-gray-700">
        You can edit a template at any time — update the document text, change variable mappings,
        adjust custom text values, or add new placeholders.
      </span>
      <span className="text-xs font-[450] text-gray-700">
        Any documents already generated from a template — whether signed, pending signing, or in
        progress — are independent copies. Changes to the template do not retroactively change them.
        Only documents generated after the edit will reflect the updated configuration.
      </span>
    </div>

    <div className="flex flex-col pt-8" id="main-step-7">
      <span id="key-notes-and-tips" />
      <h2 className="text-[16px] font-semibold leading-[2] text-gray-700">Tips</h2>
      <ul className="flex list-disc flex-col gap-2 pl-10">
        <li className="text-xs font-[450] text-gray-700">
          Use descriptive placeholder names that reflect the data or signer.
        </li>
        <li className="text-xs font-[450] text-gray-700">
          Plan your standard field tokens before writing the document. Sketch out who signs where
          and name tokens accordingly.
        </li>
        <li className="text-xs font-[450] text-gray-700">
          Leave enough space around standard fields: when placing a{' '}
          <span style={tokenFontStyle}>[standard_field]</span> token in your Word document, make
          sure there is at least one empty line above and below it. This ensures the signature,
          date, initials, or text field is placed accurately in the final document without
          overlapping or interfering with surrounding content.
        </li>
        <li className="text-xs font-[450] text-gray-700">
          Download the sample template first — even if you don't use it, it shows how placeholders
          should look in a real document.
        </li>
      </ul>
      <span className="mt-6 text-xs font-[450] text-gray-700">
        If you have any questions or need assistance preparing your template for mapping in
        CapQuest, please reach out to us at{' '}
        <a className="font-semibold text-brand-700 underline" href="mailto:support@capquest.io">
          support@capquest.io
        </a>{' '}
        — our dedicated team will be happy to help.
      </span>
    </div>
  </div>
);
